'use client';

import { cn } from '@core/utils';
import { useState, useSyncExternalStore } from 'react';

import { Image } from '@/components/client';
import { getStaticCdnUrl } from '@/utils';

const SLOW_CONNECTION_TYPES = ['slow-2g', '2g'];
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const POSTER_SRC = getStaticCdnUrl('gift-trial/hero-mascot-v3-poster.webp');

type NetworkInformation = EventTarget & {
  saveData?: boolean;
  effectiveType?: string;
};

const getConnection = () =>
  (navigator as Navigator & { connection?: NetworkInformation }).connection;

const subscribeAnimationCondition = (onChange: () => void) => {
  const connection = getConnection();
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  connection?.addEventListener('change', onChange);
  reducedMotion.addEventListener('change', onChange);

  return () => {
    connection?.removeEventListener('change', onChange);
    reducedMotion.removeEventListener('change', onChange);
  };
};

// 데이터 절약·2G 이하·모션 줄이기에선 120KB대 애니메이션 대신 포스터만 둔다
const getShouldAnimate = () => {
  const connection = getConnection();

  return (
    !connection?.saveData &&
    !SLOW_CONNECTION_TYPES.includes(connection?.effectiveType ?? '') &&
    !window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
};

export const HeroMascot = ({ className }: { className?: string }) => {
  const shouldAnimate = useSyncExternalStore(
    subscribeAnimationCondition,
    getShouldAnimate,
    () => false,
  );
  const [isAnimationReady, setIsAnimationReady] = useState(false);

  if (!shouldAnimate && isAnimationReady) {
    setIsAnimationReady(false);
  }

  const isAnimationVisible = shouldAnimate && isAnimationReady;

  return (
    <div aria-hidden className={cn('shrink-0', className)}>
      <Image
        src={POSTER_SRC}
        alt=""
        width={132}
        height={179}
        unoptimized
        loading="eager"
        className={cn(
          'size-full object-contain',
          isAnimationVisible && 'invisible',
        )}
      />
      {shouldAnimate && (
        <picture>
          {/* AVIF 미지원 브라우저는 이미 받은 포스터로 떨어져 애니메이션을 받지 않는다 */}
          <source
            type="image/avif"
            srcSet={`${getStaticCdnUrl('gift-trial/hero-mascot-v3-208.avif')} 208w, ${getStaticCdnUrl('gift-trial/hero-mascot-v3-264.avif')} 264w`}
            sizes="(max-width: 1279px) 107px, 135px"
          />
          <img
            src={POSTER_SRC}
            alt=""
            decoding="async"
            fetchPriority="low"
            onLoad={() => setIsAnimationReady(true)}
            className={cn(
              'absolute inset-0 size-full object-contain',
              !isAnimationVisible && 'invisible',
            )}
          />
        </picture>
      )}
    </div>
  );
};
